/**
 * Selection outline around the targeted block (vanilla's black hit-shape wireframe).
 *
 *   const outline = new BlockOutline();
 *   scene.add(outline.object3d);
 *   outline.show(x, y, z, boxes?)   // boxes: flat 1/16-unit boxes (x0,y0,z0,x1,y1,z1 ...), default whole cell
 *   outline.hide();
 *
 * Like vanilla, the lines trace the outside of the union of the boxes: the seam where two boxes of
 * a stair or an anvil meet flush is not drawn, the inner corner of a stair step is.
 */

import * as THREE from 'three';

const FULL = new Float32Array([0, 0, 0, 16, 16, 16]);
/** Lines sit this far off the surface, so they do not z-fight the block's own faces. */
const GROW = 0.001;
/**
 * GROW alone is not enough: a line's depth is interpolated differently from the triangles it lies
 * on, so up close the faces win at random pixels and the outline crawls as a dashed line. The
 * vertex shader therefore writes the depth of a point this fraction of the way to the camera on
 * the same view ray (the screen position is untouched), as vanilla does for its line layer.
 */
const DEPTH_PULL = 0.004;

export class BlockOutline {
  readonly object3d: THREE.LineSegments;
  private geoKey = '';

  constructor() {
    const material = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.65 });
    material.onBeforeCompile = (shader) => {
      shader.vertexShader = shader.vertexShader.replace('#include <project_vertex>', `#include <project_vertex>
        vec4 pulled = projectionMatrix * vec4(mvPosition.xyz * ${1 - DEPTH_PULL}, 1.0);
        gl_Position.z = pulled.z / pulled.w * gl_Position.w;`);
    };
    this.object3d = new THREE.LineSegments(new THREE.BufferGeometry(), material);
    this.object3d.visible = false;
  }

  /** (x, y, z) is the origin the boxes are relative to: the cell, plus a plant's jitter. */
  show(x: number, y: number, z: number, boxes: Float32Array = FULL): void {
    let key = '';
    for (let i = 0; i < boxes.length; i++) key += boxes[i] + ',';
    if (key !== this.geoKey) {
      this.geoKey = key;
      this.object3d.geometry.dispose();
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(outlineEdges(boxes), 3));
      this.object3d.geometry = g;
    }
    this.object3d.position.set(x, y, z);
    this.object3d.visible = true;
  }

  hide(): void { this.object3d.visible = false; }
}

/**
 * Line-segment endpoints (block units) of the outline of the union of `boxes`. The boxes are cut
 * into a grid on their own coordinates; a grid edge is drawn when the four grid cells around it
 * make a corner (1 or 3 filled, or 2 diagonal) rather than a flat face or nothing.
 */
export function outlineEdges(boxes: Float32Array): number[] {
  const cuts: number[][] = [[], [], []];
  for (let k = 0; k + 5 < boxes.length; k += 6) {
    for (let a = 0; a < 3; a++) cuts[a].push(boxes[k + a], boxes[k + a + 3]);
  }
  for (let a = 0; a < 3; a++) cuts[a] = [...new Set(cuts[a])].sort((p, q) => p - q);
  const n = cuts.map((c) => c.length - 1);
  const idx = [0, 0, 0];
  const filled = (): boolean => {
    for (let a = 0; a < 3; a++) if (idx[a] < 0 || idx[a] >= n[a]) return false;
    for (let k = 0; k + 5 < boxes.length; k += 6) {
      let inside = true;
      for (let a = 0; a < 3 && inside; a++) {
        const mid = (cuts[a][idx[a]] + cuts[a][idx[a] + 1]) / 2;
        inside = mid > boxes[k + a] && mid < boxes[k + a + 3];
      }
      if (inside) return true;
    }
    return false;
  };

  const out: number[] = [];
  const p = [0, 0, 0];
  for (let a = 0; a < 3; a++) {
    const b = (a + 1) % 3, c = (a + 2) % 3;
    for (let i = 0; i < n[a]; i++) {
      for (let j = 0; j <= n[b]; j++) {
        for (let k = 0; k <= n[c]; k++) {
          // the four cells around the edge: (low b, low c), (high b, low c), (low b, high c), (high b, high c)
          const f: boolean[] = [];
          for (let q = 0; q < 4; q++) {
            idx[a] = i; idx[b] = j - 1 + (q & 1); idx[c] = k - 1 + (q >> 1);
            f.push(filled());
          }
          const count = +f[0] + +f[1] + +f[2] + +f[3];
          if (count === 0 || count === 4 || (count === 2 && f[0] !== f[3])) continue;
          // nudge the line away from the filled cells
          p[b] = cuts[b][j] / 16 + GROW * Math.sign(+f[0] + +f[2] - +f[1] - +f[3]);
          p[c] = cuts[c][k] / 16 + GROW * Math.sign(+f[0] + +f[1] - +f[2] - +f[3]);
          p[a] = cuts[a][i] / 16;
          out.push(p[0], p[1], p[2]);
          p[a] = cuts[a][i + 1] / 16;
          out.push(p[0], p[1], p[2]);
        }
      }
    }
  }
  return out;
}
